import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, MapPin, Tag, Calendar, FolderOpen } from 'lucide-react';
import Navbar from '../components/Navabar';
import Footer from '../components/Footer';
import api from '../components/api';
import { getCurrentLanguage } from '../utils/translations';

const ProjectDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguage());
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleLanguageChange = () => setCurrentLanguage(getCurrentLanguage());
    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/api/projects/${slug}/`);
        setProject(response.data);
      } catch {
        try {
          const listResponse = await api.get('/api/projects/');
          const found = listResponse.data.find(
            p => p.slug === slug || String(p.id) === String(slug)
          );
          if (found) setProject(found);
          else setError(true);
        } catch {
          setError(true);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [slug]);

  const getField = (fr, en) => currentLanguage === 'en' ? (en || fr) : (fr || en);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex items-center justify-center py-32">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-[#089556] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-500 text-sm">{currentLanguage === 'fr' ? 'Chargement...' : 'Loading...'}</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-32 text-center">
          <FolderOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 text-lg mb-6">
            {currentLanguage === 'fr' ? 'Projet non trouvé.' : 'Project not found.'}
          </p>
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#089556] text-white rounded-lg hover:bg-[#078a4a] transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {currentLanguage === 'fr' ? 'Retour' : 'Back'}
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const title = getField(project.title_fr, project.title_en) || project.title || '';
  const description = getField(project.description_fr, project.description_en) || project.description || '';
  const imageUrl = project.image_url || project.image || '';

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* ── Bannière style AHB ── */}
      <section className="relative h-[280px] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#089556] via-[#07a050] to-[#07D1EB]">
          {/* Dot pattern */}
          <div className="absolute inset-0 opacity-[0.12]" style={{
            backgroundImage: `radial-gradient(circle at 20% 30%, rgba(255,255,255,0.8) 2px, transparent 2px),
                              radial-gradient(circle at 80% 70%, rgba(255,255,255,0.6) 2px, transparent 2px)`,
            backgroundSize: '120px 120px, 100px 100px'
          }} />
        </div>

        {/* Content */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center relative z-10 max-w-4xl mx-auto px-4">
            {/* Back button */}
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-1.5 text-white/70 hover:text-white text-xs mb-5 transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              {currentLanguage === 'fr' ? 'Retour aux projets' : 'Back to projects'}
            </button>

            {/* Icon badge */}
            <div className="inline-flex items-center gap-2 mb-4">
              <div className="w-9 h-9 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/30">
                <FolderOpen className="h-4 w-4 text-white" />
              </div>
              <span className="text-xs font-semibold text-white/90 uppercase tracking-wider">
                {currentLanguage === 'fr' ? 'Nos Projets' : 'Our Projects'}
              </span>
            </div>

            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 leading-tight drop-shadow-lg">
              {title}
            </h1>

            {project.stats && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-xs rounded-full border border-white/30">
                <Users className="h-3 w-3" />
                {project.stats}
              </span>
            )}

            <div className="w-20 h-1 bg-white/50 mx-auto mt-5 rounded-full" />
          </div>
        </div>
      </section>

      {/* ── Contenu principal ── */}
      <section className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">

          {/* Image */}
          {imageUrl && (
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img
                src={imageUrl}
                alt={title}
                className="w-full h-72 lg:h-96 object-cover"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          )}

          {/* Détails */}
          <div className={!imageUrl ? 'lg:col-span-2' : ''}>

            {/* Description */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <span className="w-1 h-6 bg-[#089556] rounded-full inline-block flex-shrink-0"></span>
                {currentLanguage === 'fr' ? 'À propos du projet' : 'About the project'}
              </h2>
              <div className="text-gray-700 leading-relaxed whitespace-pre-line text-sm md:text-base">
                {description}
              </div>
            </div>

            {/* Meta info */}
            {(project.category || project.location || project.date || project.stats) && (
              <div className="bg-gray-50 rounded-2xl p-5 mb-6 border border-gray-100">
                <h3 className="font-bold text-gray-700 mb-4 text-xs uppercase tracking-widest">
                  {currentLanguage === 'fr' ? 'Informations' : 'Information'}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {project.category && (
                    <div className="flex items-start gap-3">
                      <Tag className="h-4 w-4 text-[#089556] mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-400">{currentLanguage === 'fr' ? 'Catégorie' : 'Category'}</p>
                        <p className="text-sm font-medium text-gray-700">{project.category}</p>
                      </div>
                    </div>
                  )}
                  {project.location && (
                    <div className="flex items-start gap-3">
                      <MapPin className="h-4 w-4 text-[#089556] mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-400">{currentLanguage === 'fr' ? 'Localisation' : 'Location'}</p>
                        <p className="text-sm font-medium text-gray-700">{project.location}</p>
                      </div>
                    </div>
                  )}
                  {project.date && (
                    <div className="flex items-start gap-3">
                      <Calendar className="h-4 w-4 text-[#089556] mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-400">{currentLanguage === 'fr' ? 'Date' : 'Date'}</p>
                        <p className="text-sm font-medium text-gray-700">{project.date}</p>
                      </div>
                    </div>
                  )}
                  {project.stats && (
                    <div className="flex items-start gap-3">
                      <Users className="h-4 w-4 text-[#089556] mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-400">{currentLanguage === 'fr' ? 'Impact' : 'Impact'}</p>
                        <p className="text-sm font-medium text-gray-700">{project.stats}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* CTA Don */}
            <div className="bg-gradient-to-r from-[#089556] to-[#07D1EB] rounded-2xl p-5 text-white">
              <h3 className="font-bold mb-1 text-lg">
                {currentLanguage === 'fr' ? 'Soutenir ce projet' : 'Support this project'}
              </h3>
              <p className="text-sm text-white/80 mb-4">
                {currentLanguage === 'fr'
                  ? 'Votre don contribue directement à nos actions sur le terrain.'
                  : 'Your donation directly contributes to our field actions.'}
              </p>
              <button
                onClick={() => navigate(`/${currentLanguage}/don`)}
                className="bg-white text-[#089556] font-semibold px-6 py-2.5 rounded-xl hover:bg-gray-50 transition-colors text-sm"
              >
                {currentLanguage === 'fr' ? 'Faire un don' : 'Make a donation'}
              </button>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProjectDetail;
